import React, {
    ChangeEvent,
    FC,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import {
    GRAIN_LIQUID_RATIO_URL,
    deleteFetchRequest,
    getFetchRequest,
    patchFetchRequest,
    postFetchRequest,
} from '../../store/requests';
import { errorCallback, makeUrl } from '../../util';
import Title from '../Title';
import Loading from '../Loading';
import { Button, Input, Select } from '@chapche/ui';
import { PRODUCT_FORM_GRAIN, PRODUCT_FORM_LIQUID } from '../../constant';
import { KASHA_CONSISTENCY_OPTIONS } from '../../services/cookingStepConditionUtils';
import { productsService } from '../../services/productsService';
import { productsStorage } from '../../services/productsStorage';

type Translate = TFunction;
type RatioBasis = 'volume' | 'mass';
type LiquidKind = 'water' | 'milk' | 'plant-milk' | 'any';

type SelectOption = {
    value: string;
    label: string;
};

type GrainLiquidRatio = {
    id: string;
    productCode?: string | null;
    productCategory?: string | null;
    consistencyCode: string;
    liquidKind: string;
    liquidProductCode?: string | null;
    liquidPerGrain: number;
    basis: RatioBasis | string;
};

type GrainLiquidRatioWrite = {
    productCode?: string;
    productCategory?: string;
    consistencyCode: string;
    liquidKind: string;
    liquidProductCode?: string;
    liquidPerGrain: number;
    basis: RatioBasis;
};

type RatioFormState = {
    productCode: string;
    productCategory: string;
    consistencyCode: string;
    liquidKind: string;
    liquidProductCode: string;
    liquidPerGrain: string;
    basis: RatioBasis;
};

type CatalogProductForm = {
    formGroup?: string;
    formType?: string;
    formCode?: string;
    productForm?: string;
};

type CatalogProductProperty = {
    propertyName?: string;
    value?: string | number | null;
};

type CatalogProduct = {
    productCode: string;
    isDeleted?: boolean;
    productForms?: CatalogProductForm[];
    properties?: CatalogProductProperty[] | Record<string, CatalogProductProperty>;
};

type ProductsStorageLike = {
    productsList: CatalogProduct[];
    loadItems: (callback?: () => void, width?: number) => void;
};

type CsvImportResult = {
    upserted?: number;
};

type FieldSelectProps = {
    label: string;
    options: SelectOption[];
    value: string;
    onChange: (value: string | number) => void;
    placeholder?: string;
};

const EMPTY_FORM: RatioFormState = {
    productCode: '',
    productCategory: '',
    consistencyCode: 'fluffy',
    liquidKind: 'water',
    liquidProductCode: 'water',
    liquidPerGrain: '2',
    basis: 'volume',
};

const KIND_COLLAPSE_OPTIONS: readonly LiquidKind[] = ['plant-milk', 'any'];
const KNOWN_LIQUID_KINDS: readonly LiquidKind[] = ['water', 'milk', 'plant-milk', 'any'];
const LIQUID_KIND_ALIASES: Record<string, LiquidKind> = {
    'cow-milk': 'milk',
    'oat-milk': 'plant-milk',
    'almond-milk': 'plant-milk',
    'coconut-milk': 'plant-milk',
    'soy-milk': 'plant-milk',
    'rice-milk': 'plant-milk',
};
const BASIS_OPTIONS: readonly RatioBasis[] = ['volume', 'mass'];
const CATEGORY_OPTIONS: readonly string[] = ['', 'grain'];
const PRODUCT_SELECT_PREFIX = 'product:';
const KIND_SELECT_PREFIX = 'kind:';
const FORM_TYPE_HEAT_TREATMENT = 'heatTreatment';
const FORM_TYPE_PRODUCT_PART = 'product-part';
/** Already-cooked heat treatments — not dry grain for cooking ratios. `raw` is allowed. */
const COOKED_HEAT_TREATMENT_CODES: ReadonlySet<string> = new Set([
    'boiled',
    'steamed',
    'baked',
    'roasted',
    'broiled',
    'fried',
    'sauteed',
    'stewed',
    'poached',
    'microwaved',
    'sous-vide',
    'pressure-cooked',
]);
/** Product-part forms that are not cookable grain for porridge ratios (e.g. rye-flour). */
const NON_RATIO_GRAIN_PART_CODES: ReadonlySet<string> = new Set([
    'flour',
]);

const productsCatalog = productsStorage as ProductsStorageLike;

const formCodeOf = (form: CatalogProductForm): string => (
    (form.formCode || form.productForm || '').trim().toLowerCase()
);

const isProductPartGrainForm = (form: CatalogProductForm): boolean => (
    form.formGroup === PRODUCT_FORM_GRAIN
    && (!form.formType || form.formType === FORM_TYPE_PRODUCT_PART)
);

const hasCookedHeatTreatmentForm = (product: CatalogProduct): boolean => (
    Array.isArray(product.productForms)
    && product.productForms.some((form) => (
        form.formType === FORM_TYPE_HEAT_TREATMENT
        && COOKED_HEAT_TREATMENT_CODES.has(formCodeOf(form))
    ))
);

/** True if product has a cookable grain part (cereal / flakes / whole-grain / …), not only flour. */
const hasRatioGrainForm = (product: CatalogProduct): boolean => (
    Array.isArray(product.productForms)
    && product.productForms.some((form) => (
        isProductPartGrainForm(form)
        && !NON_RATIO_GRAIN_PART_CODES.has(formCodeOf(form))
    ))
);

const isGrainProduct = (product: CatalogProduct): boolean => (
    hasRatioGrainForm(product)
    && !hasCookedHeatTreatmentForm(product)
);

const isLiquidProduct = (product: CatalogProduct): boolean => (
    Array.isArray(product.productForms)
    && product.productForms.some((form) => form.formGroup === PRODUCT_FORM_LIQUID)
);

const productProperties = (product: CatalogProduct): CatalogProductProperty[] => {
    if (!product.properties) {
        return [];
    }
    if (Array.isArray(product.properties)) {
        return product.properties;
    }
    return Object.values(product.properties);
};

const deriveLiquidKind = (product: CatalogProduct | undefined, productCode: string): string => {
    if (product) {
        const fromProp = productProperties(product).find((prop) => prop.propertyName === 'liquidKind');
        if (fromProp?.value != null && String(fromProp.value).trim()) {
            return String(fromProp.value).trim().toLowerCase();
        }
    }
    const code = productCode.trim().toLowerCase();
    if (LIQUID_KIND_ALIASES[code]) {
        return LIQUID_KIND_ALIASES[code];
    }
    if ((KNOWN_LIQUID_KINDS as readonly string[]).includes(code) && code !== 'any') {
        return code;
    }
    return code || 'water';
};

const resolveProductName = (productCode: string, locale: string): string => {
    if (!productCode) {
        return '';
    }
    return productsService.getLocalization(locale, productCode, false) || '';
};

const formatProductLabel = (productCode: string | null | undefined, locale: string): string => {
    if (!productCode) {
        return '—';
    }
    const name = resolveProductName(productCode, locale);
    if (name && name !== productCode) {
        return `${name} (${productCode})`;
    }
    return productCode;
};

const liquidSelectValue = (form: RatioFormState): string => {
    if (form.liquidProductCode.trim()) {
        return `${PRODUCT_SELECT_PREFIX}${form.liquidProductCode.trim()}`;
    }
    return `${KIND_SELECT_PREFIX}${form.liquidKind.trim() || 'water'}`;
};

const applyLiquidSelect = (
    form: RatioFormState,
    value: string,
    products: CatalogProduct[],
): RatioFormState => {
    if (value.startsWith(PRODUCT_SELECT_PREFIX)) {
        const code = value.slice(PRODUCT_SELECT_PREFIX.length);
        const product = products.find((item) => item.productCode === code);
        return {
            ...form,
            liquidProductCode: code,
            liquidKind: deriveLiquidKind(product, code),
        };
    }
    if (value.startsWith(KIND_SELECT_PREFIX)) {
        return {
            ...form,
            liquidProductCode: '',
            liquidKind: value.slice(KIND_SELECT_PREFIX.length) || 'water',
        };
    }
    return form;
};

const itemToForm = (item: GrainLiquidRatio): RatioFormState => ({
    productCode: item.productCode || '',
    productCategory: item.productCategory || '',
    consistencyCode: item.consistencyCode || 'fluffy',
    liquidKind: item.liquidKind || 'water',
    liquidProductCode: item.liquidProductCode || '',
    liquidPerGrain: item.liquidPerGrain == null ? '' : String(item.liquidPerGrain),
    basis: item.basis === 'mass' ? 'mass' : 'volume',
});

const buildWriteBody = (form: RatioFormState): GrainLiquidRatioWrite => {
    const body: GrainLiquidRatioWrite = {
        consistencyCode: form.consistencyCode.trim(),
        liquidKind: form.liquidKind.trim(),
        liquidPerGrain: Number(form.liquidPerGrain),
        basis: form.basis,
    };
    if (form.productCode.trim()) {
        body.productCode = form.productCode.trim();
    }
    if (form.productCategory.trim()) {
        body.productCategory = form.productCategory.trim();
    }
    if (form.liquidProductCode.trim()) {
        body.liquidProductCode = form.liquidProductCode.trim();
    }
    return body;
};

const validateForm = (form: RatioFormState, t: Translate): string => {
    const productCode = form.productCode.trim();
    const productCategory = form.productCategory.trim();
    if (!productCode && !productCategory) {
        return t('app.page.grain-liquid-ratios.validation.productOrCategory');
    }
    if (!form.consistencyCode.trim()) {
        return t('app.page.grain-liquid-ratios.validation.consistencyRequired');
    }
    if (!form.liquidKind.trim()) {
        return t('app.page.grain-liquid-ratios.validation.liquidRequired');
    }
    const ratio = Number(form.liquidPerGrain);
    if (!Number.isFinite(ratio) || ratio <= 0) {
        return t('app.page.grain-liquid-ratios.validation.ratioPositive');
    }
    if (form.basis !== 'volume' && form.basis !== 'mass') {
        return t('app.page.grain-liquid-ratios.validation.basisInvalid');
    }
    return '';
};

const isGrainLiquidRatio = (value: unknown): value is GrainLiquidRatio => (
    typeof value === 'object'
    && value !== null
    && typeof (value as GrainLiquidRatio).id === 'string'
);

const FieldSelect: FC<FieldSelectProps> = ({
    label,
    options,
    value,
    onChange,
    placeholder,
}) => (
    <label className="input__label grain-liquid-ratios__field">
        <span className="block__text">{label}</span>
        <Select
            fullWidth
            className="mt-0"
            options={options}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
        />
    </label>
);

const GrainLiquidRatiosPage: FC = () => {
    const { i18n } = useTranslation();
    const t = i18n.t.bind(i18n) as Translate;
    const importInputRef = useRef<HTMLInputElement | null>(null);
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState<GrainLiquidRatio[]>([]);
    const [form, setForm] = useState<RatioFormState>(EMPTY_FORM);
    const [formError, setFormError] = useState('');
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editForm, setEditForm] = useState<RatioFormState>(EMPTY_FORM);
    const [editError, setEditError] = useState('');
    const [saving, setSaving] = useState(false);
    const [importStatus, setImportStatus] = useState('');
    const [importError, setImportError] = useState(false);
    const [productsLoaded, setProductsLoaded] = useState(
        (productsCatalog.productsList || []).length > 0,
    );

    useEffect(() => {
        if (productsLoaded) {
            return undefined;
        }
        let cancelled = false;
        productsCatalog.loadItems(() => {
            if (!cancelled) {
                setProductsLoaded(true);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [productsLoaded]);

    const catalogProducts = productsCatalog.productsList || [];

    const grainProductOptions = useMemo((): SelectOption[] => {
        const emptyOption: SelectOption = {
            value: '',
            label: t('app.page.grain-liquid-ratios.product.empty'),
        };
        const options = catalogProducts
            .filter((product) => !product.isDeleted && isGrainProduct(product))
            .map((product) => ({
                value: product.productCode,
                label: formatProductLabel(product.productCode, i18n.resolvedLanguage),
            }))
            .sort((a, b) => a.label.localeCompare(b.label, i18n.resolvedLanguage));
        return [emptyOption, ...options];
    }, [productsLoaded, t, i18n.resolvedLanguage, catalogProducts]);

    const productOptionsFor = useCallback((currentCode: string): SelectOption[] => {
        if (!currentCode || grainProductOptions.some((opt) => opt.value === currentCode)) {
            return grainProductOptions;
        }
        return [
            ...grainProductOptions,
            {
                value: currentCode,
                label: formatProductLabel(currentCode, i18n.resolvedLanguage),
            },
        ];
    }, [grainProductOptions, i18n.resolvedLanguage]);

    const liquidOptions = useMemo((): SelectOption[] => {
        const waterValue = `${PRODUCT_SELECT_PREFIX}water`;
        const productOptions = catalogProducts
            .filter((product) => !product.isDeleted && isLiquidProduct(product))
            .map((product) => ({
                value: `${PRODUCT_SELECT_PREFIX}${product.productCode}`,
                label: formatProductLabel(product.productCode, i18n.resolvedLanguage),
            }))
            .sort((a, b) => {
                if (a.value === waterValue) {
                    return -1;
                }
                if (b.value === waterValue) {
                    return 1;
                }
                return a.label.localeCompare(b.label, i18n.resolvedLanguage);
            });
        const kindOptions = KIND_COLLAPSE_OPTIONS.map((code) => ({
            value: `${KIND_SELECT_PREFIX}${code}`,
            label: t(`app.page.grain-liquid-ratios.liquid.kindCollapse.${code}`),
        }));
        return [...productOptions, ...kindOptions];
    }, [productsLoaded, t, i18n.resolvedLanguage, catalogProducts]);

    const liquidOptionsFor = useCallback((currentForm: RatioFormState): SelectOption[] => {
        const value = liquidSelectValue(currentForm);
        if (liquidOptions.some((opt) => opt.value === value)) {
            return liquidOptions;
        }
        if (currentForm.liquidProductCode.trim()) {
            return [
                ...liquidOptions,
                {
                    value,
                    label: formatProductLabel(currentForm.liquidProductCode, i18n.resolvedLanguage),
                },
            ];
        }
        if (currentForm.liquidKind.trim()) {
            const kind = currentForm.liquidKind.trim();
            const label = (KNOWN_LIQUID_KINDS as readonly string[]).includes(kind)
                ? t(`app.page.grain-liquid-ratios.liquid.${kind}`)
                : kind;
            return [...liquidOptions, { value, label }];
        }
        return liquidOptions;
    }, [liquidOptions, i18n.resolvedLanguage, t]);

    const consistencyOptions = useMemo(
        (): SelectOption[] => KASHA_CONSISTENCY_OPTIONS.map((code) => ({
            value: code,
            label: t(`recipe.scheme.kashaConsistency.${code}`),
        })),
        [t, i18n.resolvedLanguage],
    );

    const basisOptions = useMemo(
        (): SelectOption[] => BASIS_OPTIONS.map((code) => ({
            value: code,
            label: t(`app.page.grain-liquid-ratios.basis.${code}`),
        })),
        [t, i18n.resolvedLanguage],
    );

    const categoryOptions = useMemo(
        (): SelectOption[] => CATEGORY_OPTIONS.map((code) => ({
            value: code,
            label: code
                ? t(`app.page.grain-liquid-ratios.category.${code}`)
                : t('app.page.grain-liquid-ratios.category.empty'),
        })),
        [t, i18n.resolvedLanguage],
    );

    const load = useCallback(() => {
        setLoading(true);
        getFetchRequest(
            makeUrl([GRAIN_LIQUID_RATIO_URL]),
            (response: unknown) => {
                setItems(Array.isArray(response) ? response as GrainLiquidRatio[] : []);
                setLoading(false);
            },
            (err: unknown) => {
                setLoading(false);
                errorCallback(err);
            },
        );
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const onCreate = () => {
        const validationError = validateForm(form, t);
        if (validationError) {
            setFormError(validationError);
            return;
        }
        setFormError('');
        postFetchRequest(
            makeUrl([GRAIN_LIQUID_RATIO_URL]),
            buildWriteBody(form),
            () => {
                setForm(EMPTY_FORM);
                setFormError('');
                load();
            },
            errorCallback,
        );
    };

    const onStartEdit = (item: GrainLiquidRatio) => {
        setEditingId(item.id);
        setEditForm(itemToForm(item));
        setEditError('');
    };

    const onCancelEdit = () => {
        setEditingId(null);
        setEditForm(EMPTY_FORM);
        setEditError('');
        setSaving(false);
    };

    const onSaveEdit = () => {
        if (!editingId || saving) {
            return;
        }
        const validationError = validateForm(editForm, t);
        if (validationError) {
            setEditError(validationError);
            return;
        }
        setEditError('');
        setSaving(true);
        patchFetchRequest(
            makeUrl([GRAIN_LIQUID_RATIO_URL, `${editingId}/`]),
            buildWriteBody(editForm),
            (updated: unknown) => {
                setItems((prev) => prev.map((row) => (
                    row.id === editingId && isGrainLiquidRatio(updated)
                        ? { ...row, ...updated }
                        : row
                )));
                onCancelEdit();
                load();
            },
            (err: unknown) => {
                setSaving(false);
                errorCallback(err);
            },
        );
    };

    const onDelete = (id: string) => {
        if (editingId === id) {
            onCancelEdit();
        }
        deleteFetchRequest(
            makeUrl([GRAIN_LIQUID_RATIO_URL, `${id}/`]),
            () => load(),
            errorCallback,
        );
    };

    const onExport = () => {
        getFetchRequest(
            makeUrl([GRAIN_LIQUID_RATIO_URL, 'csv/']),
            async (response: Response) => {
                const blob = await response.blob();
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = 'grain_liquid_ratio.csv';
                link.click();
                URL.revokeObjectURL(url);
            },
            errorCallback,
            true,
        );
    };

    const onImportFile = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) {
            return;
        }
        setImportStatus('');
        setImportError(false);
        onCancelEdit();
        file.text().then((text) => {
            fetch(makeUrl([GRAIN_LIQUID_RATIO_URL, 'csv/']), {
                credentials: 'include',
                method: 'POST',
                headers: { 'Content-Type': 'text/csv; charset=utf-8' },
                body: text,
            })
                .then((response) => {
                    if (!response.ok) {
                        throw new Error('import failed');
                    }
                    return response.json() as Promise<CsvImportResult>;
                })
                .then((result) => {
                    setImportStatus(
                        t('app.page.grain-liquid-ratios.importSuccess', {
                            count: result?.upserted ?? 0,
                        }),
                    );
                    load();
                })
                .catch((err: unknown) => {
                    setImportError(true);
                    setImportStatus(t('app.page.grain-liquid-ratios.importFailed'));
                    errorCallback(err);
                });
        });
    };

    const formatConsistency = (code: string): string => (
        (KASHA_CONSISTENCY_OPTIONS as readonly string[]).includes(code)
            ? t(`recipe.scheme.kashaConsistency.${code}`)
            : code
    );

    const formatLiquidCell = (item: GrainLiquidRatio): string => {
        if (item.liquidProductCode) {
            return formatProductLabel(item.liquidProductCode, i18n.resolvedLanguage);
        }
        if ((KNOWN_LIQUID_KINDS as readonly string[]).includes(item.liquidKind)) {
            if (item.liquidKind === 'plant-milk' || item.liquidKind === 'any') {
                return t(`app.page.grain-liquid-ratios.liquid.kindCollapse.${item.liquidKind}`);
            }
            return t(`app.page.grain-liquid-ratios.liquid.${item.liquidKind}`);
        }
        return item.liquidKind;
    };

    const formatBasis = (code: string): string => (
        (BASIS_OPTIONS as readonly string[]).includes(code)
            ? t(`app.page.grain-liquid-ratios.basis.${code}`)
            : code
    );

    const formatCategory = (code: string | null | undefined): string => {
        if (!code) {
            return '—';
        }
        return t(`app.page.grain-liquid-ratios.category.${code}`, { defaultValue: code });
    };

    const formatProduct = (code: string | null | undefined): string => (
        formatProductLabel(code, i18n.resolvedLanguage)
    );

    const renderEditableRow = (item: GrainLiquidRatio) => {
        const isEditing = editingId === item.id;
        return (
            <React.Fragment key={item.id}>
                <tr className={isEditing ? 'grain-liquid-ratios__row_editing' : undefined}>
                    <td>
                        {isEditing ? (
                            <Select
                                fullWidth
                                className="mt-0"
                                options={productOptionsFor(editForm.productCode)}
                                value={editForm.productCode}
                                onChange={(value: string | number) => setEditForm({
                                    ...editForm,
                                    productCode: String(value),
                                })}
                                placeholder={t('app.page.grain-liquid-ratios.product.empty')}
                            />
                        ) : formatProduct(item.productCode)}
                    </td>
                    <td>
                        {isEditing ? (
                            <Select
                                fullWidth
                                className="mt-0"
                                options={categoryOptions}
                                value={editForm.productCategory}
                                onChange={(value: string | number) => setEditForm({
                                    ...editForm,
                                    productCategory: String(value),
                                })}
                                placeholder={t('app.page.grain-liquid-ratios.category.empty')}
                            />
                        ) : formatCategory(item.productCategory)}
                    </td>
                    <td>
                        {isEditing ? (
                            <Select
                                fullWidth
                                className="mt-0"
                                options={consistencyOptions}
                                value={editForm.consistencyCode}
                                onChange={(value: string | number) => setEditForm({
                                    ...editForm,
                                    consistencyCode: String(value),
                                })}
                            />
                        ) : formatConsistency(item.consistencyCode)}
                    </td>
                    <td>
                        {isEditing ? (
                            <Select
                                fullWidth
                                className="mt-0"
                                options={liquidOptionsFor(editForm)}
                                value={liquidSelectValue(editForm)}
                                onChange={(value: string | number) => setEditForm(
                                    applyLiquidSelect(editForm, String(value), catalogProducts),
                                )}
                            />
                        ) : formatLiquidCell(item)}
                    </td>
                    <td>
                        {isEditing ? (
                            <div className="grain-liquid-ratios__ratio-edit">
                                <span className="grain-liquid-ratios__ratio-prefix">1∶</span>
                                <Input
                                    fullWidth
                                    className="mt-0"
                                    type="number"
                                    min="0"
                                    step="0.1"
                                    value={editForm.liquidPerGrain}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => setEditForm({
                                        ...editForm,
                                        liquidPerGrain: e.target.value,
                                    })}
                                />
                            </div>
                        ) : `1∶${item.liquidPerGrain}`}
                    </td>
                    <td>
                        {isEditing ? (
                            <Select
                                fullWidth
                                className="mt-0"
                                options={basisOptions}
                                value={editForm.basis}
                                onChange={(value: string | number) => setEditForm({
                                    ...editForm,
                                    basis: String(value) === 'mass' ? 'mass' : 'volume',
                                })}
                            />
                        ) : formatBasis(item.basis)}
                    </td>
                    <td>
                        <div className="grain-liquid-ratios__row-actions">
                            {isEditing ? (
                                <>
                                    <Button
                                        type="button"
                                        size="small"
                                        disabled={saving}
                                        onClick={onSaveEdit}
                                    >
                                        {t('app.page.grain-liquid-ratios.save')}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="small"
                                        disabled={saving}
                                        onClick={onCancelEdit}
                                    >
                                        {t('app.page.grain-liquid-ratios.cancel')}
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="small"
                                        disabled={editingId != null}
                                        onClick={() => onStartEdit(item)}
                                    >
                                        {t('app.page.grain-liquid-ratios.edit')}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="small"
                                        disabled={editingId != null}
                                        onClick={() => onDelete(item.id)}
                                    >
                                        {t('app.page.grain-liquid-ratios.delete')}
                                    </Button>
                                </>
                            )}
                        </div>
                    </td>
                </tr>
                {isEditing && editError ? (
                    <tr className="grain-liquid-ratios__row_error">
                        <td colSpan={7}>
                            <p className="block__bullet error">{editError}</p>
                        </td>
                    </tr>
                ) : null}
            </React.Fragment>
        );
    };

    return (
        <section className="main__content workspace grain-liquid-ratios">
            <div className="grain-liquid-ratios__page-header">
                <div className="grain-liquid-ratios__title-block">
                    <Title titleValue={t('app.page.grain-liquid-ratios.title')} />
                    <p className="block__content grain-liquid-ratios__hint">
                        {t('app.page.grain-liquid-ratios.hint')}
                    </p>
                </div>
                <div className="grain-liquid-ratios__header-actions">
                    <Button type="button" variant="outline" onClick={onExport}>
                        {t('app.page.grain-liquid-ratios.exportCsv')}
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => importInputRef.current?.click()}
                    >
                        {t('app.page.grain-liquid-ratios.importCsv')}
                    </Button>
                    <input
                        ref={importInputRef}
                        type="file"
                        accept=".csv,text/csv"
                        hidden
                        onChange={onImportFile}
                    />
                </div>
            </div>

            {importStatus ? (
                <p className={`block__bullet ${importError ? 'error' : 'grain-liquid-ratios__import-ok'}`}>
                    {importStatus}
                </p>
            ) : null}

            <article className="grain-liquid-ratios__card">
                <h2 className="block__title-h3 grain-liquid-ratios__card-title">
                    {t('app.page.grain-liquid-ratios.addTitle')}
                </h2>
                <p className="block__content grain-liquid-ratios__card-hint">
                    {t('app.page.grain-liquid-ratios.addHint')}
                </p>
                <div className="grain-liquid-ratios__form">
                    <FieldSelect
                        label={t('app.page.grain-liquid-ratios.fields.productCode')}
                        options={productOptionsFor(form.productCode)}
                        value={form.productCode}
                        onChange={(value) => setForm({ ...form, productCode: String(value) })}
                        placeholder={t('app.page.grain-liquid-ratios.product.empty')}
                    />
                    <FieldSelect
                        label={t('app.page.grain-liquid-ratios.fields.productCategory')}
                        options={categoryOptions}
                        value={form.productCategory}
                        onChange={(value) => setForm({ ...form, productCategory: String(value) })}
                        placeholder={t('app.page.grain-liquid-ratios.category.empty')}
                    />
                    <FieldSelect
                        label={t('app.page.grain-liquid-ratios.fields.consistencyCode')}
                        options={consistencyOptions}
                        value={form.consistencyCode}
                        onChange={(value) => setForm({ ...form, consistencyCode: String(value) })}
                    />
                    <FieldSelect
                        label={t('app.page.grain-liquid-ratios.fields.liquidKind')}
                        options={liquidOptionsFor(form)}
                        value={liquidSelectValue(form)}
                        onChange={(value) => setForm(
                            applyLiquidSelect(form, String(value), catalogProducts),
                        )}
                    />
                    <Input
                        fullWidth
                        type="number"
                        min="0"
                        step="0.1"
                        label={t('app.page.grain-liquid-ratios.fields.liquidPerGrain')}
                        placeholder={t('app.page.grain-liquid-ratios.fields.liquidPerGrainPlaceholder')}
                        value={form.liquidPerGrain}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({
                            ...form,
                            liquidPerGrain: e.target.value,
                        })}
                    />
                    <FieldSelect
                        label={t('app.page.grain-liquid-ratios.fields.basis')}
                        options={basisOptions}
                        value={form.basis}
                        onChange={(value) => setForm({
                            ...form,
                            basis: String(value) === 'mass' ? 'mass' : 'volume',
                        })}
                    />
                </div>
                {formError ? <p className="block__bullet error">{formError}</p> : null}
                <div className="grain-liquid-ratios__form-actions">
                    <Button type="button" onClick={onCreate}>
                        {t('app.page.grain-liquid-ratios.create')}
                    </Button>
                </div>
            </article>

            {loading ? <Loading /> : null}

            {!loading && items.length === 0 ? (
                <p className="main__subtitle grain-liquid-ratios__empty">
                    {t('app.page.grain-liquid-ratios.empty')}
                </p>
            ) : null}

            {!loading && items.length > 0 ? (
                <div className="grain-liquid-ratios__table-wrap">
                    <table className="grain-liquid-ratios__table block__table">
                        <thead>
                            <tr>
                                <th>{t('app.page.grain-liquid-ratios.columns.product')}</th>
                                <th>{t('app.page.grain-liquid-ratios.columns.category')}</th>
                                <th>{t('app.page.grain-liquid-ratios.columns.consistency')}</th>
                                <th>{t('app.page.grain-liquid-ratios.columns.liquid')}</th>
                                <th>{t('app.page.grain-liquid-ratios.columns.ratio')}</th>
                                <th>{t('app.page.grain-liquid-ratios.columns.basis')}</th>
                                <th aria-label={t('app.page.grain-liquid-ratios.columns.actions')} />
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item) => renderEditableRow(item))}
                        </tbody>
                    </table>
                </div>
            ) : null}
        </section>
    );
};

export default GrainLiquidRatiosPage;
